import React, { useState } from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { useTranslation } from 'react-i18next';
import { Brand, Colors, Fonts, Radii, Spacing, Typography } from '../constants';
import { UserClub } from '../types';
import { PageBand } from './PageBand';
import { StatsBadge } from './StatsBadge';
import { SearchInput } from './SearchInput';
import { ClubFilterDropdown } from './ClubFilterDropdown';
import { Button } from './Button';

/** Width of the club filter inside the band toolbar. */
const BAND_FILTER_WIDTH = 260;

interface WelcomeHeroProps {
  userName: string;
  itemCount: number;
  newItemCount?: number;
  userClubCount?: number;
  onSearch: (term: string) => void;
}

/**
 * `HomeBand` adds the toolbar's club filter and "Browse all" button, plus the
 * club names that become the band's overline kicker.
 */
interface HomeBandProps extends WelcomeHeroProps {
  userClubs?: UserClub[];
  selectedClubId?: string;
  onClubFilter: (clubId: string, currentSearchTerm: string) => void;
  onBrowseAll: () => void;
  /** Names of the user's clubs — joined by " · " as the kicker. */
  clubNames?: string[];
  /** Counts not in yet: the stats slot shows a skeleton of the same height. */
  loading?: boolean;
}

/** "Welcome back, *Sarah*" — the first name in Lora italic. */
const useGreeting = (userName: string) => {
  const { t } = useTranslation();
  const trimmed = (userName || '').trim();
  const firstName = trimmed === '' ? t('components.welcomeHero.guest') : (trimmed.split(' ')[0] || t('components.welcomeHero.guest'));
  return {
    welcomeBack: t('components.welcomeHero.welcomeBack'),
    displayName: firstName.length > 20 ? `${firstName.substring(0, 17)}…` : firstName,
  };
};

/**
 * Home's green page band (design v2, desktop). Composes `PageBand` from the
 * club names (kicker), the display greeting with the first name in saliemist
 * italic, the numeral stats `onDark`, and the straddling toolbar: search,
 * club filter and the secondary "Browse all" button.
 *
 * Mobile renders `WelcomeHero` instead — there is no band on mobile.
 */
export const HomeBand: React.FC<HomeBandProps> = ({
  userName,
  itemCount,
  newItemCount = 0,
  userClubCount = 0,
  userClubs = [],
  clubNames = [],
  selectedClubId = '',
  onSearch,
  onClubFilter,
  onBrowseAll,
  loading = false,
}) => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const { welcomeBack, displayName } = useGreeting(userName);

  return (
    <PageBand
      display
      testID="home-band"
      kicker={clubNames.length > 0 ? clubNames.join(' · ') : undefined}
      title={
        <Text style={[Typography.display, styles.bandGreeting]} numberOfLines={1}>
          {welcomeBack} <Text style={styles.bandGreetingName}>{displayName}</Text>
        </Text>
      }
      right={
        loading ? (
          <View style={styles.bandStats}>
            <View style={styles.statSkeleton} />
            <View style={styles.statSkeleton} />
          </View>
        ) : (
        <View style={styles.bandStats}>
          <StatsBadge onDark value={itemCount} label={t('components.welcomeHero.items')} />
          {newItemCount > 0 && (
            <StatsBadge
              onDark
              value={newItemCount}
              label={t('components.welcomeHero.newToday')}
              sublabel={t('components.welcomeHero.last24h')}
            />
          )}
          <StatsBadge onDark value={userClubCount} label={t('components.welcomeHero.clubs')} />
        </View>
        )
      }
      toolbar={
        <>
          <SearchInput
            bordered
            value={searchTerm}
            onChangeText={setSearchTerm}
            onSearch={onSearch}
            placeholder={t('components.welcomeHero.searchPlaceholder')}
            testID="hero-search"
          />
          {userClubs.length > 0 && (
            <ClubFilterDropdown
              userClubs={userClubs}
              value={selectedClubId}
              onSelect={(clubId) => onClubFilter(clubId, searchTerm)}
              width={BAND_FILTER_WIDTH}
            />
          )}
          <Button
            title={t('components.welcomeHero.browseAll')}
            variant="secondary"
            onPress={onBrowseAll}
            style={styles.browseButton}
          />
        </>
      }
    />
  );
};

/**
 * Home greeting on mobile (design v2): white, no panel. The display greeting
 * with the first name in accent-strong italic, a row of numeral stats, then
 * the bordered search field.
 *
 * On desktop the greeting lives on the green band — use `HomeBand`.
 */
export const WelcomeHero: React.FC<WelcomeHeroProps> = ({
  userName,
  itemCount,
  newItemCount = 0,
  userClubCount = 0,
  onSearch,
}) => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const { welcomeBack, displayName } = useGreeting(userName);

  return (
    <View style={styles.mobile}>
      <Text style={[Typography.displayMobile, styles.greeting]} numberOfLines={2}>
        {welcomeBack} <Text style={styles.greetingName}>{displayName}</Text>
      </Text>

      <View style={styles.stats}>
        <StatsBadge value={itemCount} label={t('components.welcomeHero.items')} />
        {newItemCount > 0 && (
          <StatsBadge
            value={newItemCount}
            label={t('components.welcomeHero.newToday')}
            sublabel={t('components.welcomeHero.last24h')}
          />
        )}
        <StatsBadge value={userClubCount} label={t('components.welcomeHero.clubs')} />
      </View>

      <SearchInput
        bordered
        value={searchTerm}
        onChangeText={setSearchTerm}
        onSearch={onSearch}
        placeholder={t('components.welcomeHero.searchPlaceholder')}
        style={styles.search}
        testID="hero-search"
      />
    </View>
  );
};

const styles = StyleSheet.create({
  // ── Desktop band ──────────────────────────────────────────────────────────
  bandGreeting: {
    color: Colors.white,
  },
  bandGreetingName: {
    fontFamily: Fonts.displayItalic,
    color: Brand.saliemist,
  },
  bandStats: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: Spacing['2xl'],
    flexShrink: 0,
    paddingBottom: 6,
  },
  statSkeleton: {
    width: 64,
    height: 24,
    borderRadius: Radii.tag,
    backgroundColor: 'rgba(255, 255, 255, 0.14)',
  },
  browseButton: {
    flexShrink: 0,
  },

  // ── Mobile ────────────────────────────────────────────────────────────────
  mobile: {
    backgroundColor: Colors.white,
  },
  greeting: {
    color: Colors.textDark,
  },
  greetingName: {
    fontFamily: Fonts.displayItalic,
    color: Colors.accentStrong,
  },
  stats: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: Spacing.lg,
    marginTop: Spacing.md,
  },
  search: {
    flexGrow: 0,
    marginTop: Spacing.lg,
  },
});
